import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Field, inputCls } from "./FormBits";

const ENTITY_LABELS = { limited_company: "Limited company", sole_recruiter: "Sole recruiter", partnership: "Partnership" };
const MOTIVATION_LABELS = { more_roles: "More roles to work on", candidates: "Candidates for my roles", both: "Both", looking: "Having a look" };

const initial = {
  entity_type: "limited_company", trading_name: "", company_registration_number: "", first_name: "", last_name: "",
  business_email: "", phone: "", country: "Ireland", coverage: "Local", recruitment_niches: [], motivation: "", website: "",
};

const Summary = ({ label, value }) => (
  <div className="flex justify-between gap-6 border-b border-border/70 py-2.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium text-charcoal">{value || "—"}</span>
  </div>
);

export const PartnerApplicationForm = () => {
  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/pc/options").then(({ data }) => setOpts(data)).catch(() => {}); }, []);

  const validate = () => {
    const e = {};
    if (form.trading_name.trim().length < 2) e.trading_name = "Enter your company name.";
    if (form.first_name.trim().length < 1) e.first_name = "Required.";
    if (form.last_name.trim().length < 1) e.last_name = "Required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.business_email)) e.business_email = "Enter a valid business email.";
    if (form.phone.trim().length < 5) e.phone = "Enter a contact number.";
    if (form.recruitment_niches.length === 0) e.recruitment_niches = "Select at least one niche.";
    if (!form.motivation) e.motivation = "Please choose one option.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/pc/applications", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (done)
    return (
      <div className="rounded-2xl border border-success/30 bg-success/5 p-8 text-center" data-testid="pc-application-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
        <h3 className="mt-4 font-serif text-2xl text-navy">Your application has been submitted.</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">We will review your details and be in touch via your business email.</p>
      </div>
    );

  if (step === 2)
    return (
      <div data-testid="pc-step-2">
        <p className="overline text-xs text-gold">Step 2 of 2 — Review and submit</p>
        <div className="mt-4">
          <Summary label="Business type" value={ENTITY_LABELS[form.entity_type]} />
          <Summary label="Company" value={`${form.trading_name}${form.company_registration_number ? ` · ${form.company_registration_number}` : ""}`} />
          <Summary label="Name" value={`${form.first_name} ${form.last_name}`} />
          <Summary label="Business email" value={form.business_email} />
          <Summary label="Phone / WhatsApp" value={form.phone} />
          <Summary label="Country" value={form.country} />
          <Summary label="Coverage" value={form.coverage} />
          <Summary label="Recruitment niche" value={form.recruitment_niches.join(", ")} />
          <Summary label="What brings you here?" value={MOTIVATION_LABELS[form.motivation]} />
        </div>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button type="button" onClick={submit} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-6 py-3 text-sm font-semibold text-white hover:bg-gold-dark disabled:opacity-70" data-testid="pc-submit-application">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Submit Application
          </button>
          <button type="button" onClick={() => setStep(1)} className="rounded-lg border border-navy/20 px-6 py-3 text-sm font-semibold text-navy" data-testid="pc-back">Back</button>
        </div>
      </div>
    );

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (validate()) setStep(2); }} className="space-y-5" data-testid="pc-step-1" noValidate>
      <p className="overline text-xs text-gold">Step 1 of 2 — Business details</p>
      <div className="grid grid-cols-3 gap-2 rounded-lg bg-secondary p-1" role="tablist">
        {Object.entries(ENTITY_LABELS).map(([k, l]) => (
          <button type="button" key={k} onClick={() => set("entity_type", k)} className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${form.entity_type === k ? "bg-navy text-white" : "text-charcoal/70 hover:text-navy"}`} data-testid={`pc-entity-${k}`}>{l}</button>
        ))}
      </div>
      <Field label="Company name and registration number" required error={errors.trading_name}>
        <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
          <input className={inputCls} placeholder="Search by company name or number..." value={form.trading_name} onChange={(e) => set("trading_name", e.target.value)} data-testid="pc-trading-name" />
          <input className={inputCls} placeholder="Registration no." value={form.company_registration_number} onChange={(e) => set("company_registration_number", e.target.value)} data-testid="pc-reg-number" />
        </div>
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="First name" required error={errors.first_name}><input className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="pc-first-name" /></Field>
        <Field label="Last name" required error={errors.last_name}><input className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="pc-last-name" /></Field>
        <Field label="Business email" required error={errors.business_email}><input type="email" className={inputCls} value={form.business_email} onChange={(e) => set("business_email", e.target.value)} data-testid="pc-email" /></Field>
        <Field label="Phone / WhatsApp" required error={errors.phone}><input type="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="pc-phone" /></Field>
        <Field label="Country" required>
          <select className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="pc-country">
            {["Ireland", "United Kingdom", "Germany", "Netherlands", "France", "Spain", "Italy", "Poland", "United Arab Emirates", "Saudi Arabia", "India", "Philippines", "Australia", "Canada", "United States", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Coverage" required>
          <select className={inputCls} value={form.coverage} onChange={(e) => set("coverage", e.target.value)} data-testid="pc-coverage">
            {(opts?.coverages || ["Local", "Regional", "National", "International"]).map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Recruitment niche (select all that apply)" required error={errors.recruitment_niches}>
        <div className="grid gap-2 sm:grid-cols-3">
          {(opts?.niches || []).map((n) => (
            <label key={n} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-charcoal">
              <input type="checkbox" className="h-4 w-4 accent-navy" checked={form.recruitment_niches.includes(n)} onChange={(e) => set("recruitment_niches", e.target.checked ? [...form.recruitment_niches, n] : form.recruitment_niches.filter((x) => x !== n))} data-testid={`pc-niche-${n.replace(/[^a-z]+/gi, "-").toLowerCase()}`} /> {n}
            </label>
          ))}
        </div>
      </Field>
      <Field label="What brings you here?" required error={errors.motivation}>
        <div className="grid gap-2">
          {Object.entries(MOTIVATION_LABELS).map(([k, l]) => (
            <button type="button" key={k} onClick={() => set("motivation", k)} className={`rounded-lg border px-4 py-2.5 text-left text-sm font-medium transition-colors ${form.motivation === k ? "border-navy bg-navy text-white" : "border-border text-charcoal hover:border-navy/40"}`} data-testid={`pc-motivation-${k}`}>{l}</button>
          ))}
        </div>
      </Field>
      <button type="submit" className="inline-flex w-full items-center justify-center rounded-lg bg-gold px-6 py-3 text-sm font-semibold text-white hover:bg-gold-dark sm:w-auto" data-testid="pc-continue">Continue</button>
      <p className="text-xs leading-relaxed text-muted-foreground">Open to recruitment businesses across sectors, markets and geographies. Every application is reviewed — company, identity, and professional standing — before you can share or see network activity.</p>
    </form>
  );
};

export default PartnerApplicationForm;
